import { html } from "lit";
import { formatHtml } from "@trumpf/ux.common-storybook";
import { ifDefined } from "lit/directives/if-defined.js";

export default {
    title: "Components/Event Calendar",
    parameters: {
        docs: {
            source: {
                transform: formatHtml,
            },
        },
    },
    component: "ux-iws-event-calendar",
    args: {
        compact: false,
        currentDate: "",
        endpoint:
            "https://www.trumpf.com/api/common/events/1.0/showFilteredEvents/?ttContentUid=21404&keywords=2,10,11,74,75,76,297",
        eventType: "exhibition",
        hideFutureEvents: false,
        locale: "de",
        maxEvents: 0,
        regionLabel: "Region",
        regionPlaceholder: "Weltweit",
        hallLabel: "Halle",
        boothLabel: "Stand",
    },
    argTypes: {
        compact: { control: "boolean" },
        currentDate: { control: "text" },
        endpoint: { control: "text" },
        eventType: {
            control: { type: "select" },
            options: ["career", "exhibition", "press"],
        },
        hideFutureEvents: { control: "boolean" },
        locale: { control: "text" },
        maxEvents: { control: "number" },
        regionLabel: { control: "text" },
        regionPlaceholder: { control: "text" },
        hallLabel: { control: "text" },
        boothLabel: { control: "text" },
    },
};

const Template = ({
    compact,
    currentDate,
    endpoint,
    eventType,
    hideFutureEvents,
    locale,
    maxEvents,
    regionLabel,
    regionPlaceholder,
    boothLabel,
    hallLabel,
}) =>
    html` <ux-iws-event-calendar
        compact=${ifDefined(compact)}
        current-date=${ifDefined(currentDate)}
        endpoint=${ifDefined(endpoint)}
        event-type=${ifDefined(eventType)}
        locale=${ifDefined(locale)}
        max-events=${ifDefined(maxEvents)}
        hide-future-events=${ifDefined(hideFutureEvents)}
        region-label=${ifDefined(regionLabel)}
        region-placeholder=${ifDefined(regionPlaceholder)}
        booth-label=${ifDefined(boothLabel)}
        hall-label=${ifDefined(hallLabel)}
    >
        <!-- input elements with required request parameters -->
        <div slot="form">
            <input type="hidden" name="keywords" value="2,10,11,74,75,76,297" />
        </div>
        <!-- content that should be displayed above the filter tags -->
        <div slot="filter">
            <span class="ux-label">Sie interessieren sich für</span>
        </div>
        <!-- content that should be displayed if no event matches the current filter settings -->
        <div slot="no-results">
            <p class="ux-text">
                Es wurden keine passenden Veranstaltungen gefunden.
            </p>
        </div>
    </ux-iws-event-calendar>`;

const EventCalendarInTabs = ({
    compact,
    currentDate,
    endpoint,
    eventTypes = ["exhibition", "press", "career"],
    hideFutureEvents,
    locale,
    maxEvents,
    regionLabel,
    regionPlaceholder,
    boothLabel,
    hallLabel,
}) => {
    const tabs = eventTypes.map(
        (eventType) => html`
            <ux-tab name="${eventType}" tab-id="tab-${eventType}">
                ${Template({
                    compact,
                    currentDate,
                    endpoint,
                    eventType,
                    hideFutureEvents,
                    locale,
                    maxEvents,
                    regionLabel,
                    regionPlaceholder,
                    boothLabel,
                    hallLabel,
                })}
            </ux-tab>
        `
    );
    return html`
        <ux-tab-container id="event-calendar-tabs" scroll-when-activated>
            ${tabs.map((tab) => tab)}
        </ux-tab-container>
    `;
};

const EventCalendarStaticTemplate = ({
    compact,
    currentDate,
    endpoint,
    hideFutureEvents,
    locale,
    maxEvents,
    regionLabel,
    regionPlaceholder,
    boothLabel,
    hallLabel,
}) => html`
    <ux-iws-event-calendar
        compact=${ifDefined(compact)}
        current-date=${ifDefined(currentDate)}
        endpoint=${ifDefined(endpoint)}
        hide-future-events=${ifDefined(hideFutureEvents)}
        locale=${ifDefined(locale)}
        max-events=${ifDefined(maxEvents)}
        region-label=${ifDefined(regionLabel)}
        region-placeholder=${ifDefined(regionPlaceholder)}
        booth-label=${ifDefined(boothLabel)}
        hall-label=${ifDefined(hallLabel)}
    >
        <ux-iws-event-calendar-item
            itemscope=""
            itemtype="https://schema.org/Event"
        >
            <div class="ux-iws-event-calendar-item__date-container">
                <time itemprop="startDate" datetime="2024-07-03">
                    <div class="ux-iws-event-calendar-item__year">2024</div>
                    <div class="ux-iws-event-calendar-item__month">Jul</div>
                    <div class="ux-iws-event-calendar-item__date">03 – 05</div>
                </time>
            </div>
            <div class="ux-iws-event-calendar-item__content-container">
                <div class="ux-iws-event-calendar-item__booth">
                    Halle A Stand 09
                </div>
                <div class="ux-iws-event-calendar-item__title" itemprop="name">
                    DIC EXPO - DISPLAY INNOVATION CONVENTION 2024
                </div>
                <div class="ux-iws-event-calendar-item__tags">
                    <ux-tag>Laser Technology</ux-tag>
                </div>
                <div
                    class="ux-iws-event-calendar-item__location"
                    itemprop="location"
                    itemscope=""
                    itemtype="https://schema.org/Place"
                >
                    <div
                        itemprop="addressCountry"
                        itemscope=""
                        itemtype="https://schema.org/PostalAddress"
                    >
                        China
                    </div>
                </div>
                <div class="ux-iws-event-calendar-item__links">
                    <a
                        class="ux-button"
                        href="http://www.dicexpo.com/"
                        target="_blank"
                    >
                        Messe, Ausstellung
                    </a>
                </div>
            </div>
        </ux-iws-event-calendar-item>
    </ux-iws-event-calendar>
`;

export const Default = Template.bind({});
Default.args = {
    currentDate: "2024-03-01",
    endpoint: "./mock-data/event-calendar.json",
    locale: "de",
    regionLabel: "Region",
    regionPlaceholder: "Weltweit",
};

export const EventCalendarTabs = EventCalendarInTabs.bind({});
EventCalendarTabs.args = {
    ...Default.args,
    maxEvents: 5,
};

export const EventCalendarStatic = EventCalendarStaticTemplate.bind({});
EventCalendarStatic.args = {
    endpoint:
        "https://www.trumpf.com/api/common/events/1.0/showFilteredEvents/?ttContentUid=21404&keywords=2,10,11,74,75,76,297",
    locale: "de",
    compact: true,
    maxEvents: 5,
};

export const EventCalendarHideFutureEvents = EventCalendarInTabs.bind({});
EventCalendarHideFutureEvents.args = {
    locale: "de",
    regionLabel: "Region",
    regionPlaceholder: "Weltweit",
    hideFutureEvents: true,
};
